import React, { useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Upload as UploadIcon, Mail, Database, Loader2, AlertCircle, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { rassegne, Rassegna } from '../lib/data';
const providerMeta: Record<string, {
  label: string;
  icon: ComponentType<any>;
}> = {
  mimesi: {
    label: 'Mimesi',
    icon: Database
  },
  eco_stampa: {
    label: "L'Eco della Stampa",
    icon: Database
  },
  data_stampa: {
    label: 'Data Stampa',
    icon: Database
  },
  email: {
    label: 'Email',
    icon: Mail
  },
  upload: {
    label: 'Upload manuale',
    icon: UploadIcon
  }
};
function StatusInline({
  status
}: {
  status: Rassegna['status'];
}) {
  if (status === 'processing') {
    return <div className="flex items-center gap-1.5 text-xs text-blue-600" data-id="element-1349">
        <Loader2 className="h-3 w-3 animate-spin" data-id="element-1350" />
        <span data-id="element-1351">In elaborazione</span>
      </div>;
  }
  if (status === 'error') {
    return <div className="flex items-center gap-1.5 text-xs text-destructive" data-id="element-1352">
        <AlertCircle className="h-3 w-3" data-id="element-1353" />
        <span data-id="element-1354">Errore</span>
      </div>;
  }
  return null;
}
function UploadDialog({
  open,
  onOpenChange
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  return <Dialog open={open} onOpenChange={onOpenChange} data-id="element-1355">
      <DialogContent className="sm:max-w-[480px]" data-id="element-1356">
        <DialogHeader data-id="element-1357">
          <DialogTitle data-id="element-1358">Nuova rassegna manuale</DialogTitle>
          <DialogDescription data-id="element-1359">
            Carica un file PDF. L'AI estrarrà automaticamente gli articoli.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4" data-id="element-1360">
          <div className="border-2 border-dashed border-border rounded-md p-10 text-center hover:border-accent transition-colors cursor-pointer" data-id="element-1361">
            <UploadIcon className="h-6 w-6 text-muted-foreground mx-auto mb-3" data-id="element-1362" />
            <p className="text-sm font-medium mb-1" data-id="element-1363">Trascina il PDF qui</p>
            <p className="text-xs text-muted-foreground" data-id="element-1364">
              oppure clicca per selezionare
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2" data-id="element-1365">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-accent text-accent hover:bg-accent/10 hover:text-accent" data-id="element-1366">
            Annulla
          </Button>
          <Button onClick={() => onOpenChange(false)} className="rounded-full" data-id="element-1367">
            Carica
          </Button>
        </div>
      </DialogContent>
    </Dialog>;
}
export function Rassegne() {
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'auto' | 'manual'>('all');
  const filtered = rassegne.filter(r => {
    if (filter === 'all') return true;
    return r.source === filter;
  });
  return <div className="px-6 py-6 max-w-[1400px] mx-auto w-full" data-id="element-1368">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-center justify-between mb-10 md:mb-12 gap-4" data-id="element-1369">
        <div data-id="element-1370">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-id="element-1371">
            Rassegne
          </h1>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="rounded-full h-11 px-6 text-sm" data-id="element-1372">
          <Plus className="h-4 w-4 mr-1.5" data-id="element-1373" />
          Nuova rassegna
        </Button>
      </header>

      {/* Filter tabs — minimal text-based */}
      <div className="bg-card border border-border rounded-lg" data-id="element-1374">
        <div className="flex items-center gap-6 px-6 pt-5 pb-0 text-sm" data-id="element-1375">
          {(['all', 'auto', 'manual'] as const).map(f => {
          const count = f === 'all' ? rassegne.length : rassegne.filter(r => r.source === f).length;
          const label = f === 'all' ? 'Tutte' : f === 'auto' ? 'Automatiche' : 'Manuali';
          const active = filter === f;
          return <button key={f} onClick={() => setFilter(f)} className={`pb-3 border-b-2 transition-colors ${active ? 'border-accent text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-id="element-1376">
                {label}{' '}
                <span className="text-muted-foreground ml-1" data-id="element-1377">({count})</span>
              </button>;
        })}
        </div>

        <Separator data-id="element-1378" />

        {/* Rassegne list */}
        {filtered.length === 0 ? <div className="text-center py-16" data-id="element-1379">
            <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-3" data-id="element-1380" />
            <p className="text-sm text-muted-foreground" data-id="element-1381">
              Nessuna rassegna in questo filtro
            </p>
          </div> : <div className="divide-y divide-border" data-id="element-1382">
            {filtered.map(r => {
          const meta = providerMeta[r.provider];
          const Icon = meta.icon;
          return <button key={r.id} onClick={() => navigate(`/rassegne/${r.id}`)} className="w-full text-left py-5 md:py-6 px-6 hover:bg-muted/30 transition-colors" data-id="element-1383">
                  <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8" data-id="element-1384">
                    {/* Left: info */}
                    <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-1385">
                      <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1386">
                        <Icon className="h-4 w-4 text-muted-foreground" data-id="element-1387" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1388">
                        <h3 className="mb-1 truncate font-[400] text-[14px]" data-id="element-1389">
                          {r.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground" data-id="element-1390">
                          <span data-id="element-1391">{meta.label}</span>
                          <span data-id="element-1392">·</span>
                          <span data-id="element-1393">{r.dateRange}</span>
                          {r.status !== 'processed' && <>
                              <span data-id="element-1394">·</span>
                              <StatusInline status={r.status} data-id="element-1395" />
                            </>}
                        </div>
                      </div>
                    </div>

                    {/* Right: minimal metrics */}
                    <div className="flex items-center gap-8 md:gap-12 flex-shrink-0 pl-14 md:pl-0" data-id="element-1396">
                      <div className="text-left md:text-right" data-id="element-1397">
                        <div className="text-xl md:text-2xl leading-none font-[400]" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1398">
                          {r.articleCount}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1" data-id="element-1399">
                          Articoli
                        </div>
                      </div>
                      <div className="text-left md:text-right" data-id="element-1400">
                        <div className="text-xl md:text-2xl text-accent leading-none font-[400]" style={{
                    fontVariantNumeric: 'tabular-nums'
                  }} data-id="element-1401">
                          {r.avgScore.toFixed(1)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1" data-id="element-1402">
                          Score medio
                        </div>
                      </div>
                    </div>
                  </div>
                </button>;
        })}
          </div>}
      </div>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} data-id="element-1403" />
    </div>;
}